import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  CallDirection,
  CallPeriod,
  CompanyCallLogsFilters,
} from '@/api/callLogs'

interface Props {
  filters: CompanyCallLogsFilters
  onChange: (next: CompanyCallLogsFilters) => void
}

export function HistoryFilters({ filters, onChange }: Props) {
  const { t } = useTranslation()

  const set = (patch: Partial<CompanyCallLogsFilters>) => {
    onChange({ ...filters, ...patch, page: 1 })
  }

  const reset = () => {
    onChange({
      period: 'all',
      status: 'all',
      direction: 'all',
      answeredBy: 'all',
      startDate: '',
      endDate: '',
      page: 1,
      pageSize: filters.pageSize ?? 25,
    })
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {t('manager.history.filters.period')}
        </Label>
        <Select
          value={filters.period ?? 'all'}
          onValueChange={(v) => set({ period: v as CallPeriod })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('manager.history.filters.allPeriod')}</SelectItem>
            <SelectItem value="today">{t('manager.history.filters.today')}</SelectItem>
            <SelectItem value="week">{t('manager.history.filters.last7Days')}</SelectItem>
            <SelectItem value="month">{t('manager.history.filters.last30Days')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {t('manager.history.filters.status')}
        </Label>
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(v) => set({ status: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('manager.history.filters.allStatuses')}</SelectItem>
            <SelectItem value="completed">{t('manager.history.statuses.completed')}</SelectItem>
            <SelectItem value="missed">{t('manager.history.statuses.missed')}</SelectItem>
            <SelectItem value="no-answer">{t('manager.history.statuses.noAnswer')}</SelectItem>
            <SelectItem value="busy">{t('manager.history.statuses.busy')}</SelectItem>
            <SelectItem value="failed">{t('manager.history.statuses.failed')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {t('manager.history.filters.direction')}
        </Label>
        <Select
          value={filters.direction ?? 'all'}
          onValueChange={(v) =>
            set({ direction: v === 'all' ? 'all' : (v as CallDirection) })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('manager.history.filters.inboundAndOutbound')}</SelectItem>
            <SelectItem value="inbound">{t('manager.history.filters.inbound')}</SelectItem>
            <SelectItem value="outbound">{t('manager.history.filters.outbound')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {t('manager.history.filters.answeredBy')}
        </Label>
        <Select
          value={filters.answeredBy ?? 'all'}
          onValueChange={(v) => set({ answeredBy: v as 'all' | 'ai' })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('manager.history.filters.answeredByAll')}</SelectItem>
            <SelectItem value="ai">{t('manager.history.filters.answeredByAi')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-end">
        <Button
          type="button"
          variant="outline"
          onClick={reset}
          className="w-full"
        >
          {t('manager.history.filters.reset')}
        </Button>
      </div>
    </div>
  )
}
